import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../css/desktop/DesktopBetSlipPanel.css';
import { useLang } from '../../i18n';
import { useGeo } from '../../geo';

const API = process.env.REACT_APP_API_BASE_URL;

function Trash() {
  return (
    <svg viewBox="0 0 14 15" width="14" height="15" fill="none">
      <path
        d="M2 3.5h10M5.5 3V1.8h3V3M3 3.5l.6 9a1 1 0 001 .9h4.8a1 1 0 001-.9l.6-9M6 6v5M8 6v5"
        stroke="#757490"
        strokeWidth="1.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function money(n) { return (Math.round(n * 100) / 100).toFixed(2); }

export default function DesktopBetSlipPanel() {
  const { t } = useLang();
  const { betChips, symbol, rate } = useGeo().prices;
  const [bets, setBets] = useState(() =>
    JSON.parse(localStorage.getItem('betSlipItems') || '[]')
  );
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('user');
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => setBets(JSON.parse(localStorage.getItem('betSlipItems') || '[]'));
    window.addEventListener('betAdded', load);
    return () => window.removeEventListener('betAdded', load);
  }, []);

  useEffect(() => {
    const sync = () => {
      try { setUser(JSON.parse(localStorage.getItem('user'))); } catch { setUser(null); }
    };
    window.addEventListener('userUpdated', sync);
    window.addEventListener('storage',     sync);
    return () => {
      window.removeEventListener('userUpdated', sync);
      window.removeEventListener('storage',     sync);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('betSlipItems', JSON.stringify(bets));
  }, [bets]);

  const setStake = (id, val) =>
    setBets(prev => prev.map(b => b.id === id ? { ...b, stake: parseFloat(val) || 0 } : b));

  const removeBet = (id) => {
    const updated = bets.filter(b => b.id !== id);
    setBets(updated);
    localStorage.setItem('betSlipItems', JSON.stringify(updated));
    window.dispatchEvent(new Event('betAdded'));
  };

  const clearAll = () => {
    setBets([]);
    localStorage.removeItem('betSlipItems');
    window.dispatchEvent(new Event('betAdded'));
  };

  const totalOdds   = bets.reduce((p, b) => p * b.odds, 1);
  const totalStake  = bets.reduce((s, b) => s + (b.stake || 0), 0);
  const potentialWin = totalStake * totalOdds;
  const canPlace    = totalStake >= rate;

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const placeBet = async () => {
    if (!user)     { showToast('error', t('betslip.loginTobet')); setTimeout(() => navigate('/auth'), 1500); return; }
    if (!canPlace) { showToast('error', t('betslip.minimumStake', { min: `${rate.toFixed(2)} ${symbol}` })); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/bets/place.php`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ user_id: user.id, bets: bets.map(b => ({ ...b, stake: b.stake ? parseFloat((b.stake / rate).toFixed(2)) : 0 })) }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('success', t('betslip.betPlaced'));
        const updated = { ...user, solde_actuel: data.data.nouveau_solde };
        localStorage.setItem('user', JSON.stringify(updated));
        setUser(updated);
        window.dispatchEvent(new Event('userUpdated'));
        clearAll();
      } else {
        showToast('error', data.message || t('common.error'));
      }
    } catch {
      showToast('error', t('common.serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dbsp">

      {/* ── Header ── */}
      <div className="dbsp-head">
        <span className="dbsp-title">
          Betslip
          {bets.length > 0 && <span className="dbsp-count">{bets.length}</span>}
        </span>
        {bets.length > 0 && (
          <button className="dbsp-clear" onClick={clearAll}>{t('betslip.clearAll')}</button>
        )}
      </div>

      {/* ── Corps ── */}
      <div className="dbsp-body">
        {bets.length === 0 ? (
          <div className="dbsp-empty">
            <svg viewBox="0 0 24 24" width="38" height="38" fill="none">
              <path
                d="M5 3.2h11a1 1 0 011 1V20l-2-1.3L13 20l-2-1.3L9 20l-2-1.3L5 20V4.2a1 1 0 010-1z"
                fill="rgba(255,255,255,.04)"
              />
              <path
                d="M7.5 7.5h7M7.5 10.5h7M7.5 13.5h4"
                stroke="#2a2850"
                strokeWidth="1.1"
                strokeLinecap="round"
              />
            </svg>
            <p>{t('betslip.empty')}</p>
            <span>{t('betslip.emptySub')}</span>
          </div>
        ) : (
          bets.map(b => (
            <div className="dbsp-sel" key={b.id}>
              <div className="dbsp-sel-top">
                <div className="dbsp-sel-info">
                  <div className="dbsp-sel-pick">{b.betLabel || b.pick || b.selection}</div>
                  <div className="dbsp-sel-match">{b.match}</div>
                  {(b.betType || b.market) && (
                    <div className="dbsp-sel-market">{b.betType || b.market}</div>
                  )}
                </div>
                <div className="dbsp-sel-right">
                  <span className="dbsp-sel-odds">{b.odds.toFixed(2)}</span>
                  <button className="dbsp-del" onClick={() => removeBet(b.id)}>
                    <Trash />
                  </button>
                </div>
              </div>

              <div className="dbsp-stake-row">
                <input
                  type="number"
                  className="dbsp-stake-input"
                  placeholder={t('betslip.stakePlaceholder', { symbol })}
                  value={b.stake || ''}
                  min="0"
                  step="1"
                  onChange={e => setStake(b.id, e.target.value)}
                />
              </div>

              <div className="dbsp-chips">
                {betChips.map(v => (
                  <button key={v} className="dbsp-chip" onClick={() => setStake(b.id, v)}>
                    {v}{symbol}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Récapitulatif + CTA ── */}
      {bets.length > 0 && (
        <div className="dbsp-footer">
          <div className="dbsp-summary">
            <div className="dbsp-row">
              <span className="k">{t('betslip.numberOfBets')}</span>
              <span className="v">{bets.length}</span>
            </div>
            <div className="dbsp-row">
              <span className="k">{t('betslip.totalOdds')}</span>
              <span className="v">{money(totalOdds)}</span>
            </div>
            <div className="dbsp-row">
              <span className="k">{t('betslip.totalStake')}</span>
              <span className="v">{money(totalStake)} {symbol}</span>
            </div>
            <div className="dbsp-row dbsp-row-win">
              <span className="k">{t('betslip.potentialWin')}</span>
              <span className="v win">{money(potentialWin)} {symbol}</span>
            </div>
          </div>

          <button
            className="dbsp-place"
            disabled={!canPlace || loading}
            onClick={placeBet}
          >
            {loading ? t('betslip.processing') : t('betslip.placeBet')}
          </button>
        </div>
      )}

      {toast && (
        <div className={'dbsp-toast dbsp-toast--' + toast.type}>{toast.msg}</div>
      )}
    </div>
  );
}
